import { useLayoutEffect, useRef, useState } from "react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { countFittingSkills } from "@/utils/fitSkills";

// Tailwind `gap-1.5` between badges, in px. The rows below use it too.
const GAP = 6;

const MORE_CLASS =
  "shrink-0 px-3 py-1.5 text-xs border border-dashed border-background-tertiary bg-background-primary rounded-md text-text-secondary hover:text-text-primary hover:bg-background-tertiary/20 transition whitespace-nowrap";

/**
 * A job's skills on one line: as many as fit, then "+N more", which opens the
 * full list wrapped (and "Show less" folds it back). A hidden copy of the row
 * gives each badge's real width, so the fit follows the actual font and
 * padding rather than a fixed count.
 *
 * Used by the admin job card, the candidate job listing and the job detail
 * page, so the three read the same.
 *
 * @param {string[]} skills
 * @param {string} [badgeClassName] - passed to each Badge as `className`
 * @param {string} [badgeStyle]     - passed to each Badge as `badgeStyle`
 * @param {string} [className]      - on the outer wrapper (spacing, width)
 */
const OneLineSkills = ({ skills: skillsProp, badgeClassName, badgeStyle, className }) => {
  const skills = skillsProp ?? [];
  const rowRef = useRef(null);
  const measureRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [fitting, setFitting] = useState(skills.length);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    if (!row || !measure) return undefined;

    const calculate = () => {
      const widths = [...measure.children].map((el) => el.offsetWidth);
      const moreWidth = widths.pop() ?? 0;
      setFitting(countFittingSkills(widths, row.clientWidth, moreWidth, GAP));
    };

    calculate();
    const observer = new ResizeObserver(calculate);
    observer.observe(row);
    return () => observer.disconnect();
  }, [skills]);

  if (!skills.length) return null;

  const hasMore = skills.length > fitting;
  const visible = expanded ? skills : skills.slice(0, fitting);

  const badge = (skill, i) => (
    <Badge key={i} label={skill} variant="gradient" colorIndex={i % 8} className={badgeClassName} badgeStyle={badgeStyle} />
  );

  return (
    <div className={cn("relative min-w-0 overflow-hidden", className)}>
      <div ref={rowRef} className={cn("flex items-center gap-1.5", expanded ? "flex-wrap" : "flex-nowrap overflow-hidden")}>
        {visible.map(badge)}
        {(hasMore || expanded) && (
          <Button type="button" onClick={() => setExpanded((p) => !p)} className={MORE_CLASS}>
            {expanded ? "Show less" : `+${skills.length - fitting} more`}
          </Button>
        )}
      </div>

      {/* Measuring copy: every badge, then the widest "+N more". */}
      <div ref={measureRef} aria-hidden="true" className="invisible pointer-events-none absolute left-0 top-0 flex flex-nowrap gap-1.5">
        {skills.map(badge)}
        <span className={MORE_CLASS}>{`+${skills.length} more`}</span>
      </div>
    </div>
  );
};

export default OneLineSkills;
